import { memo, useState } from 'react';
import { TextField, Box, Button, Typography } from '@mui/material';
// Local files
import BaseDialog from 'components/Common/Feedback/BaseDialog/BaseDialog';
import useCustomSelector from 'hooks/useCustomSelector';
import useClinicians from 'hooks/useClinicians'
import useSuccess from 'hooks/useSuccess';
import { cliniciansSelectors } from 'features/clinicians';

const ClinicianPrompts = ({ open, onClose }) => {
  const { setSuccess } = useSuccess();
  const { updateClinician } = useClinicians();
  const fetchedClinician = useCustomSelector(cliniciansSelectors.getClinicianData);
  const processing = useCustomSelector(cliniciansSelectors.clinicianIsFetching);
  const [filterPrompt, setFilterPrompt] = useState({ value: '', error: '' });
  const [mergePrompt, setMergePrompt] = useState({ value: '', error: '' });
  const [prompt, setPrompt] = useState({ value: '', error: '' });

  const handleEntering = () => { 
    setFilterPrompt({ value: fetchedClinician.filter_prompt ?? '', error: '' });
    setMergePrompt({ value: fetchedClinician.merge_prompt ?? '', error: '' });
    setPrompt({ value: fetchedClinician.prompt ?? '', error: '' });
  };
  
  const handleExited = () => { 
    setFilterPrompt({ value: '', error: '' });
    setMergePrompt({ value: '', error: '' });
    setPrompt({ value: '', error: '' });
  };
  const handleSave = () => {
    let hasError = false;

    if (!prompt.value) {
      setPrompt(prev => ({ ...prev, error: `Value can't be empty` }) );
      hasError = true;
    }
    
    if (hasError) return;
    
    const clinician = { 
      id: fetchedClinician.id,
      prompt: prompt.value, 
      filter_prompt: filterPrompt.value,
      merge_prompt: mergePrompt.value
    };

    if (fetchedClinician.prompt !== prompt.value || fetchedClinician.filter_prompt !== filterPrompt.value || fetchedClinician.merge_prompt !== mergePrompt.value) {
      updateClinician(clinician)
      .then(({ error }) => {
        if (!error) {
          setSuccess('Prompts successfully updated');
          onClose();
        }
      })
    } else {
      onClose();
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      otherPaperStyle={{ height: '75vh', width: '100%', overflow: 'hidden', maxWidth: 900 }}
      title={'Prompts'}
      TransitionProps={{ onExited: handleExited, onEntering: handleEntering }}
      sx={{ overflow: 'hidden' }}
      actions={
        <>
          <Button
            disabled={processing || !prompt.value}
            variant='outlined'
            color='primary'
            onClick={handleSave}
            sx={{ fontSize: '12px', px: '28px' }}
          >
            Save
          </Button>
          <Button
            disabled={processing}
            variant='outlined'
            color='error'
            onClick={onClose}
            sx={{ fontSize: '12px', px: '28px' }}
          >
            Cancel
          </Button>
        </>
      }
    >
      <Box sx={{ px: 3, pb: 3, overflowY: 'auto', width: '100%', height: '100%', mb: 4 }}>
        <TextField
          fullWidth
          margin='normal'
          label='Filter Prompt'
          multiline
          minRows={5}
          disabled={processing}
          helperText={filterPrompt.error}
          error={!!filterPrompt.error}
          value={filterPrompt.value}
          onChange={({ target: { value } }) => setFilterPrompt({ value, error: '' })}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <Typography textAlign='center' variant='caption'>
            {'{appointment_type} {patient_type} {previous_consulted}'}
          </Typography>
        </Box>
        <TextField
          fullWidth
          margin='normal'
          label='Merge Prompt'
          multiline
          minRows={5}
          disabled={processing}
          helperText={mergePrompt.error}
          error={!!mergePrompt.error}
          value={mergePrompt.value}
          onChange={({ target: { value } }) => setMergePrompt({ value, error: '' })}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <Typography textAlign='center' variant='caption'>
            {'{appointment_type} {patient_type} {previous_consulted}'}
          </Typography>
        </Box>
        <TextField
          fullWidth
          margin='normal'
          label='Scheduling Rules'
          multiline
          minRows={5}
          disabled={processing}
          helperText={prompt.error}
          error={!!prompt.error}
          value={prompt.value}
          onChange={({ target: { value } }) => setPrompt({ value, error: '' })}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <Typography textAlign='center' variant='caption'>
            {'{appointment_type} {patient_type} {previous_consulted}'}
          </Typography>
        </Box>
      </Box>
    </BaseDialog>
  );
};

export default memo(ClinicianPrompts);